import React, { useEffect, useState } from "react";
import { bool } from "prop-types";
import { push } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import MainContainer from "pages/_components/MainContainer";
import { ResponsiveHeader } from "pages/_components/header";
import Notification from "pages/_components/Notification";
import Selector from "pages/_components/fields/formik/Selector";
import FieldLabel from "pages/_components/fields/FieldLabel";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import * as i18n from "util/i18n";
import * as Yup from "yup";
import { actions as debinActions, types } from "reducers/debin";
import { useDispatch, useSelector } from "react-redux";
import useSchema from "hooks/useSchema";
import GridTable from "pages/_components/GridTable/GridTable";
import Applicant from "../_components/Applicant";

const FORM_ID = "debin.addrecurence";

const AddRecurencePage = ({ isDesktop }) => {
    const {
        fetching,
        accounts,
        concepts,
        seller,
        prestaciones,
        debinPrestacion,
        beneficiary: _beneficiary,
    } = useSelector((state) => state.debin);

    const dispatch = useDispatch();
    const { stringRequired } = useSchema();
    const [prestacionesCargadas, setPrestacionesCargadas] = useState();
    const hasBeneficiary = _beneficiary?.document;

    useEffect(() => {
        dispatch(debinActions.debinPreActivity());
    }, []);

    let sellerAccount;
    if (_beneficiary?.cuenta) {
        sellerAccount = _beneficiary?.cuenta[0];
    }
    const beneficiary = {
        email: _beneficiary?.contacto?.email,
        account: _beneficiary?.cbu,
        document: _beneficiary?.document,
        headline: _beneficiary?.headline,
        idFrequentDestination: sellerAccount?.tipo,
        key: sellerAccount?.tipo,
        isOwn: sellerAccount?.endpointId,
    };

    const initialValues = {
        productId: debinPrestacion?.productId || "",
        service: debinPrestacion?.service || "",
        reference: debinPrestacion?.reference || "",
        concept: debinPrestacion?.concept || "VAR",
        description: debinPrestacion?.description || "",
    };

    const conceptsOptions = () =>
        concepts.map((concept) => ({
            value: concept?.id,
            label: `${concept?.name}`,
        }));

    const accountOptions = () =>
        accounts.map((account) => ({
            balance: { currency: account?.currency, quantity: account?.availableBalance },
            currency: account?.currency,
            frequentDestination: null,
            id: account?.productId,
            label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(
                -4,
            )} ${i18n.get(`currency.label.${account?.currency}`)}`,
            minimumPayment: 0,
            permissions: Object.keys(account?.permissions),
            statementsLoan: null,
            totalPayment: 0,
            type: account?.accountType,
        }));

    useEffect(() => {
        if (prestaciones) {
            setPrestacionesCargadas(
                prestaciones.map((prestacion) => ({
                    label: prestacion.nombre,
                    value: prestacion.nombre,
                    reference: prestacion.ayuda_referencia,
                })),
            );
        } else {
            setPrestacionesCargadas([{ label: "", value: "" }]);
        }
    }, [prestaciones]);

    const handleReference = (selected) => {
        const prestacionReference = prestaciones.find((object) => object.nombre === selected);
        return prestacionReference.ayuda_referencia;
    };

    const handleSubmit = (values) => {
        const prestacionSeleccionada = prestaciones.find((object) => object.nombre === values.prestacion);
        const selectedAccount = accounts.find((object) => object.productId === values.productId);
        dispatch(
            debinActions.setdebinPrestacionData({
                ...values,
                selectedAccount,
                prestacionSeleccionada,
                seller,
                beneficiary,
            }),
        );
        dispatch(push("/debin/AddRecurence/confirm"));
    };

    const emitDebinValidation = Yup.lazy(() => {
        const validate = {
            productId: stringRequired,
            service: stringRequired,
            reference: stringRequired,
            concept: stringRequired,
            description: stringRequired,
        };
        return Yup.object().shape(validate);
    });

    return (
        <>
            <Notification scopeToShow="debinPayments" />
            <ResponsiveHeader id="debin.AddRecurence" backLinkTo="/debinPayments" />
            <MainContainer className="debin__add-recurence" showLoader={fetching}>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={emitDebinValidation}>
                    {({ values, setFieldValue }) => (
                        <Form>
                            <GridTable className="form">
                                <GridTable.Container columns={12} columnsTemplate="fixed">
                                    <GridTable.Data columnStart={2} columnWidth={10} className="required">
                                        <Field
                                            component={ProductSelector}
                                            idForm={FORM_ID}
                                            name="productId"
                                            label={`${i18n.get("debin.dateExpitation")}`}
                                            placeholder={`${i18n.get("global.select.account.placeholder")}`}
                                            data={{
                                                emptyOptionLabel: "",
                                                options: accountOptions(),
                                            }}
                                            isDesktop={isDesktop}
                                            onChange={(productId) =>
                                                dispatch(
                                                    debinActions.setdebinPrestacionData({
                                                        ...values,
                                                        productId,
                                                    }),
                                                )
                                            }
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={2} columnWidth={10}>
                                        <h5>{i18n.get(`${FORM_ID}.beneficiary`)}</h5>
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={2} columnWidth={10} flexDirection="column">
                                        <Applicant
                                            isDesktop={isDesktop}
                                            idForm={FORM_ID}
                                            setEmailValues={setFieldValue}
                                            beneficiaryData={_beneficiary}
                                            beneficiary={seller?.cuenta ? seller?.cuenta[0]?.cbu : ""}
                                            actionType={types.SELECT_BENEFICIARY}
                                            seller={seller}
                                            values={values}
                                            debinPrestacion={debinPrestacion}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={2} columnWidth={5} className="required">
                                        <Field
                                            component={Selector}
                                            idForm={FORM_ID}
                                            name="service"
                                            disabled={!hasBeneficiary}
                                            onChange={(selected) =>
                                                setFieldValue("reference", handleReference(selected))
                                            }
                                            options={prestacionesCargadas}
                                            placeholder={`${i18n.get("global.select.option.placeholder")}`}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={7} columnWidth={5} className="required">
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="reference"
                                            disabled={!hasBeneficiary}
                                            type="text"
                                            maxLength="100"
                                            hidePlaceholder
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={2} columnWidth={5} className="required">
                                        <Field
                                            component={Selector}
                                            idForm={FORM_ID}
                                            name="concept"
                                            disabled={!hasBeneficiary}
                                            options={conceptsOptions()}
                                            placeholder={`${i18n.get("global.select.option.placeholder")}`}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={7} columnWidth={5} className="required">
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="description"
                                            disabled={!hasBeneficiary}
                                            type="text"
                                            maxLength="100"
                                            hidePlaceholder
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>
                            <GridTable>
                                <GridTable.Container columns={12} columnsTemplate="fixed" className="form-actions">
                                    <GridTable.Data columnStart={isDesktop ? 5 : 1} columnWidth={isDesktop ? 4 : 12}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            disabled={
                                                !values.productId ||
                                                !values.concept ||
                                                !values.service ||
                                                !values.description
                                            }
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </>
    );
};

AddRecurencePage.propTypes = {
    isDesktop: bool.isRequired,
};

export default AddRecurencePage;
